const isBrowser = typeof window !== 'undefined'

/**
 * A localStorage record that carries an unfinished piece of registration to a later
 * session: `{ email, userId, attempts, ts }`, self-clearing once it has been tried too
 * often or sat too long. localStorage rather than sessionStorage because the session that
 * finishes the job is usually a later one, in a different tab.
 *
 * Every access is wrapped: storage throws outright in private-mode Safari and when a
 * quota is full, and a marker is never important enough to break a page over.
 */
export function createLocalMarker({ key, maxAttempts, maxAgeMs, isValid }) {
  const remove = () => {
    if (!isBrowser) return
    try {
      window.localStorage.removeItem(key)
    } catch {
      /* no-op */
    }
  }

  const read = () => {
    if (!isBrowser) return null
    try {
      const raw = window.localStorage.getItem(key)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (!parsed || typeof parsed !== 'object') return null
      if (!parsed.userId || !parsed.email) return null

      const ts = typeof parsed.ts === 'number' ? parsed.ts : 0
      const attempts = typeof parsed.attempts === 'number' ? parsed.attempts : 0
      // A marker that has expired, run out of attempts, or no longer satisfies the
      // caller's own rule must not be replayed.
      if (attempts >= maxAttempts || (ts && Date.now() - ts > maxAgeMs) || isValid?.(parsed) === false) {
        remove()
        return null
      }

      return { email: String(parsed.email), userId: String(parsed.userId), attempts, ts }
    } catch {
      return null
    }
  }

  const store = ({ email, userId, attempts = 0 }) => {
    if (!isBrowser || !email || !userId) return
    if (isValid?.({ email, userId }) === false) return
    try {
      window.localStorage.setItem(key, JSON.stringify({
        email: String(email).trim(),
        userId,
        attempts,
        ts: Date.now()
      }))
    } catch {
      /* no-op */
    }
  }

  return { key, read, store, clear: remove }
}
